import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {MessageInterface} from '../models/chat';
import {distinctUntilChanged, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MessageManagementService {
  private messagesSubject = new BehaviorSubject<MessageInterface[]>([]);
  private messages$ = this.messagesSubject.asObservable();


  constructor() { }

  // Add a new message to the storage
  addMessage(message: MessageInterface): void {
    const currentMessages = this.messagesSubject.value;
    const existingIndex = currentMessages.findIndex(existing =>
      this.areMessagesEquivalent(existing, message)
    );

    if (existingIndex !== -1) {
      const updatedMessages = [...currentMessages];
      updatedMessages[existingIndex] = {
        ...updatedMessages[existingIndex],
        ...message
      };
      this.messagesSubject.next(updatedMessages);
      return;
    }

    this.messagesSubject.next([...currentMessages, message]);
  }

  // Retrieve all messages as an observable
  getMessages(): Observable<MessageInterface[]> {
    return this.messages$;
  }

  // Clear all messages
  clearMessages(): void {
    this.messagesSubject.next([]);
  }

  private areMessagesEquivalent(first: MessageInterface, second: MessageInterface): boolean {
    if (first.messageId && second.messageId) {
      return first.messageId === second.messageId;
    }

    return first.conversationId === second.conversationId &&
      first.type === second.type &&
      first.messageType === second.messageType &&
      first.content === second.content &&
      this.normalizeDate(first.datePending) === this.normalizeDate(second.datePending) &&
      this.normalizeDate(first.timestamp) === this.normalizeDate(second.timestamp);
  }

  private normalizeDate(value?: Date): string {
    if (!value) {
      return '';
    }

    return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
  }

  onMessageConversationIdReceived(conversationId: string): Observable<MessageInterface | undefined> {
    return this.messages$.pipe(
      map((messages: MessageInterface[]) =>
        messages
          .filter(message => message.conversationId === conversationId && message.datePending)
          .sort((a, b) => {
            const dateA = a.datePending instanceof Date ? a.datePending : new Date(a.datePending || 0);
            const dateB = b.datePending instanceof Date ? b.datePending : new Date(b.datePending || 0);
            return dateA.getTime() - dateB.getTime();
          })
          .pop() // Get the last message after sorting
      ),
      distinctUntilChanged((prev, curr) => prev?.messageId === curr?.messageId) // Ensure distinct messages
    );
  }

  getLastMessageByConversationId(conversationId: string): Observable<MessageInterface | undefined> {
    return this.messages$.pipe(
      map((messages: MessageInterface[]) =>
        messages
          .filter(message => message.conversationId === conversationId && message.datePending)
          .sort((a, b) => {
            const dateA = a.datePending instanceof Date ? a.datePending : new Date(a.datePending || 0);
            const dateB = b.datePending instanceof Date ? b.datePending : new Date(b.datePending || 0);
            return dateB.getTime() - dateA.getTime(); // Sort by most recent
          })
          .shift() // Get the first message after sorting
      )
    );
  }

}
